/**
 * Cloud Operations — Deploy Networking tab.
 *
 * Two side-by-side panels per the unPeg reference:
 *   left:  Ingress & Load Balancing (controller, custom domain, TLS cert, CORS, rate limiting)
 *   right: Service Mesh (mTLS mode, CIDRs, network policies, egress filtering)
 *
 * State lives on the parent DeployHomePage form; this component is a
 * controlled-input editor. Posted as part of the single quick-deploy POST.
 */

import React from 'react';
import { T, inp, Field } from './_ui.jsx';
import { Toggle } from './DeployConfigureTab.jsx';

export default function DeployNetworkingTab({ form, updateNet }) {
  const n = form.networking;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 16,
      alignItems: 'start',
    }}>
      <Panel icon="▦" title="Ingress & Load Balancing" accentBg={T.bgAlt}>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Ingress Controller">
            <select
              style={inp}
              value={n.ingress_controller}
              onChange={(e) => updateNet({ ingress_controller: e.target.value })}
            >
              <option value="nginx">nginx</option>
              <option value="gce">GCP Load Balancer (gce)</option>
              <option value="alb">AWS ALB</option>
              <option value="azure-application-gateway">Azure App Gateway</option>
              <option value="istio">istio</option>
            </select>
          </Field>
          <Field label="Custom Domain" hint="Resolved CNAME → ingress IP after deploy.">
            <input
              style={inp}
              placeholder="api.yourdomain.com"
              value={n.custom_domain}
              onChange={(e) => updateNet({ custom_domain: e.target.value })}
            />
          </Field>
          <Field label="TLS Certificate ARN / Thumbprint" hint="Optional — Prove7 Cloud auto-provisions if empty.">
            <input
              style={inp}
              placeholder="arn:aws:acm:…"
              value={n.tls_cert_arn}
              onChange={(e) => updateNet({ tls_cert_arn: e.target.value })}
            />
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <Toggle
            first
            label="CORS"
            hint="Allow cross-origin requests"
            value={n.cors}
            onChange={(v) => updateNet({ cors: v })}
          />
          <Toggle
            label="Rate Limiting"
            hint="Global API rate limits"
            value={n.rate_limiting}
            onChange={(v) => updateNet({ rate_limiting: v })}
          />
        </div>
      </Panel>

      <Panel icon="⊙" title="Service Mesh" accentBg="#eef4ff">
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="mTLS Mode">
            <select
              style={inp}
              value={n.mtls_mode}
              onChange={(e) => updateNet({ mtls_mode: e.target.value })}
            >
              <option value="strict">strict</option>
              <option value="permissive">permissive</option>
              <option value="disabled">disabled</option>
            </select>
          </Field>
          <Field label="Service CIDR">
            <input
              style={inp}
              value={n.service_cidr}
              onChange={(e) => updateNet({ service_cidr: e.target.value })}
            />
          </Field>
          <Field label="Pod CIDR">
            <input
              style={inp}
              value={n.pod_cidr}
              onChange={(e) => updateNet({ pod_cidr: e.target.value })}
            />
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <Toggle
            first
            label="Network Policies"
            hint="Restrict pod-to-pod traffic"
            value={n.network_policies}
            onChange={(v) => updateNet({ network_policies: v })}
          />
          <Toggle
            label="Egress Filtering"
            hint="Control outbound traffic"
            value={n.egress_filtering}
            onChange={(v) => updateNet({ egress_filtering: v })}
          />
        </div>
      </Panel>
    </div>
  );
}

function Panel({ icon, title, accentBg, children }) {
  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.stroke}`,
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        background: accentBg,
        padding: '14px 18px',
        borderBottom: `1px solid ${T.stroke2}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ width: 24, height: 24, color: T.ink, fontSize: 14, fontWeight: 700,
          display: 'grid', placeItems: 'center' }}>{icon}</span>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</div>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}
